/**
 * PublicChurchPage — `/church/:slug` per-church website
 * =======================================================
 *
 * Hub-and-spoke architecture (matches backend `routes/church_public.py`):
 *   • Per-church: hero, about, gallery, events, contact, donate target
 *   • Shared (HQ): bookshop link (single global storefront),
 *                  newsletter signup (single global subscriber list)
 *
 * Renders one page from a single API call (`/api/church-page/<slug>`).
 * No auth required — anyone visiting the church URL sees the site.
 */
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { UpcomingCalendarSection } from '@/pages/PublicChurchCalendarPage';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Calendar, MapPin, Phone, Mail, Heart, BookOpen, Facebook, Youtube, Instagram,
  MessageCircle, ChevronRight, Loader2, Send, Globe, Church, Users, ArrowRight, LogIn,
} from 'lucide-react';
import ChurchCtaButtons from '@/components/church/ChurchCtaButtons';
import WatchLiveLink from '@/components/church/WatchLiveLink';
import { NearestAssemblyFinder } from '@/components/church/NearestAssemblyFinder';
import { MotherGeneralTributes } from '@/components/church/MotherGeneralTributes';
import { FwmHero } from '@/components/fwm/FwmHero';
import { FWM_IMAGERY, resolveImage } from '@/components/fwm/imagery';

const API = process.env.REACT_APP_BACKEND_URL;

const fetchJson = async (path) => {
  const r = await fetch(`${API}${path}`);
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.detail || `HTTP ${r.status}`);
  }
  return r.json();
};

const PublicChurchPage = () => {
  const { slug } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['church-page', slug],
    queryFn: () => fetchJson(`/api/church-page/${slug}`),
    retry: false,
  });

  if (isLoading) return <PageLoading />;
  if (error) return <PageNotFound slug={slug} message={error.message} />;
  if (!data) return null;
  // Groupings never render a congregation profile.
  if (data.is_grouping) return <GroupingNotice payload={data} />;

  const { church, hero, about, events, bookshop, newsletter, gallery, contact, donate,
          branding } = data;
  // Every church page wears the FWM house style — cream ground, ink text,
  // serif display type. The church's own colours only tint the accents.
  const dark = false;
  const themeStyle = {
    '--cp-primary': branding?.primary_color || hero?.primary_color || '#1C2A63',
    '--cp-accent': branding?.secondary_color || hero?.accent_color || '#C9992B',
  };

  return (
    <div style={themeStyle} data-branding-style="light"
         className="fwm-page min-h-screen"
         data-testid="public-church-page">
      <ChurchNav church={church} branding={branding} dark={dark} />
      <Hero hero={hero} church={church} donate={donate} branding={branding} />
      <ChurchCrest church={church} branding={branding} contact={contact} />
      <AboutSection about={about} />
      <WelcomeBand church={church} />
      {events?.enabled && <EventsSection events={events} />}
      {gallery?.enabled && <GallerySection gallery={gallery} />}
      {bookshop?.enabled !== false && <BookshopTeaser bookshop={bookshop} />}
      {newsletter?.enabled !== false && <NewsletterSignup newsletter={newsletter} sourceSlug={slug} />}
      <UpcomingCalendarSection slug={slug} dark={dark} />
      <TeamSection churchId={church.id} churchName={church.name} dark={dark} />
      <MotherGeneralTributes />
      <ContactSection contact={contact} church={church} donate={donate} />
      <Footer church={church} contact={contact} />
    </div>
  );
};

/* ─────────────── States ─────────────── */

const PageLoading = () => (
  <div className="fwm-page min-h-screen flex items-center justify-center" data-testid="church-page-loading">
    <Loader2 className="h-8 w-8 text-[color:var(--fwm-accent)] animate-spin" />
  </div>
);

const PageNotFound = ({ slug, message }) => (
  <div className="fwm-page min-h-screen flex items-center justify-center px-4" data-testid="church-page-404">
    <div className="text-center max-w-md">
      <Church className="h-12 w-12 text-[color:var(--fwm-muted)] mx-auto mb-5" />
      <h1 className="fwm-display text-2xl">Page not found</h1>
      <p className="text-sm text-[color:var(--fwm-body)] mt-3">
        No church is published at <span className="font-mono">/church/{slug}</span>.
      </p>
      <p className="text-xs text-[color:var(--fwm-muted)] mt-4">{message}</p>
      <Link to="/churches" className="fwm-btn mt-8" data-testid="church-404-directory">
        Browse the directory <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </div>
);

/** A province / district / HQ / country office is a grouping, not a place to
 *  visit — its URL introduces the leadership and lists where and when the
 *  congregations beneath it meet. */
const GroupingNotice = ({ payload }) => {
  const g = payload.grouping || {};
  const congregations = payload.congregations || [];
  const events = payload.upcoming_events || [];
  return (
    <div className="fwm-page min-h-screen" data-testid="church-grouping-notice">
      <div className="max-w-5xl mx-auto px-4 py-16 md:py-24">
        <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--fwm-muted)]">
          {g.label}
        </p>
        <h1 className="fwm-display text-3xl md:text-4xl mt-3" data-testid="grouping-name">{g.name}</h1>
        {g.overseer_name && (
          <p className="text-sm text-[color:var(--fwm-muted)] mt-3" data-testid="grouping-overseer">
            {g.overseer_title || 'Overseer'} · {g.overseer_name}
          </p>
        )}
        <p className="text-base text-[color:var(--fwm-body)] mt-5 max-w-2xl" data-testid="grouping-message">
          {g.description || payload.message}
        </p>

        {(g.contact_email || g.contact_phone || g.website_url) && (
          <div className="flex flex-wrap gap-4 mt-6 text-sm text-[color:var(--fwm-body)]"
               data-testid="grouping-contact">
            {g.contact_email && (
              <a href={`mailto:${g.contact_email}`} className="inline-flex items-center gap-2 hover:underline">
                <Mail className="h-4 w-4 text-[color:var(--fwm-accent)]" /> {g.contact_email}
              </a>
            )}
            {g.contact_phone && (
              <a href={`tel:${g.contact_phone}`} className="inline-flex items-center gap-2 hover:underline">
                <Phone className="h-4 w-4 text-[color:var(--fwm-accent)]" /> {g.contact_phone}
              </a>
            )}
            {g.website_url && (
              <a href={g.website_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 hover:underline">
                <Globe className="h-4 w-4 text-[color:var(--fwm-accent)]" /> Website
              </a>
            )}
          </div>
        )}

        {congregations.length > 0 ? (
          <div className="mt-14">
            <h2 className="text-lg font-semibold text-[color:var(--fwm-ink)]">
              Where to worship
              <span className="text-[color:var(--fwm-muted)] font-normal"> · {payload.congregation_count} congregations</span>
            </h2>
            <NearestAssemblyFinder congregations={congregations} label={g.label} />
          </div>
        ) : (
          <p className="text-sm text-[color:var(--fwm-muted)] mt-10" data-testid="grouping-empty">
            No congregations are listed under this {g.label} yet.
          </p>
        )}

        {events.length > 0 && (
          <div className="mt-14" data-testid="grouping-events">
            <h2 className="text-lg font-semibold text-[color:var(--fwm-ink)]">
              Coming up across the {g.label}
            </h2>
            <div className="mt-5 divide-y divide-[color:var(--fwm-rule)] border-t border-[color:var(--fwm-rule)]">
              {events.map((e, i) => (
                <div key={i} className="py-3 flex items-start justify-between gap-4"
                     data-testid={`grouping-event-${i}`}>
                  <div className="min-w-0">
                    {e.event_slug || e.event_id ? (
                      <Link to={e.event_slug ? `/e/${e.event_slug}` : `/events/${e.event_id}`}
                            className="text-sm font-medium text-[color:var(--fwm-ink)] truncate hover:underline"
                            data-testid={`grouping-event-${i}-link`}>
                        {e.title}
                      </Link>
                    ) : (
                      <p className="text-sm font-medium text-[color:var(--fwm-ink)] truncate">{e.title}</p>
                    )}
                    <p className="text-xs text-[color:var(--fwm-muted)] mt-0.5">
                      {[e.church_name ? `Hosted by ${e.church_name}` : '', e.location]
                        .filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-medium text-[color:var(--fwm-body)]">{e.date}</p>
                    <p className="text-[11px] text-[color:var(--fwm-muted)]">{e.start_time || ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Link to="/churches" className="fwm-btn mt-12" data-testid="grouping-directory-link">
          Browse the church directory <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

/* ────────────── Sections ────────────── */

/** Our Team — pastors, elders and team leaders. Photo, name and role only. */
const TeamSection = ({ churchId, churchName, dark }) => {
  const { data } = useQuery({
    queryKey: ['church-public-team', churchId],
    queryFn: () => fetchJson(`/api/church/public/${churchId}/leadership`),
    enabled: !!churchId,
    retry: false,
  });
  const team = data?.team || [];
  if (team.length === 0) return null;

  return (
    <section id="team" className={`py-16 ${dark ? 'bg-[#12161d]' : 'bg-white'}`}
             data-testid="church-team-directory">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--cp-primary)' }}>
          Our Team
        </h2>
        <p className={`mt-1 mb-8 text-sm ${dark ? 'text-[color:var(--fwm-muted)]' : 'text-[color:var(--fwm-muted)]'}`}>
          The people who serve and lead at {churchName}.
        </p>
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">
          {team.map((p, i) => (
            <div key={`${p.name}-${i}`} className="text-center" data-testid={`team-member-${i}`}>
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.name}
                     className="mx-auto h-24 w-24 rounded-full object-cover ring-2 ring-black/5" />
              ) : (
                <div className={`mx-auto grid h-24 w-24 place-items-center rounded-full text-lg font-semibold
                                 ${dark ? 'bg-slate-800 text-slate-300' : 'bg-[color:var(--fwm-ground-2)] text-[color:var(--fwm-muted)]'}`}>
                  {p.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                </div>
              )}
              <p className={`mt-3 font-medium ${dark ? 'text-slate-100' : 'text-[color:var(--fwm-ink)]'}`}>{p.name}</p>
              <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--cp-accent)' }}>
                {p.role}
              </p>
              {p.bio && (
                <p className={`mt-2 text-xs leading-relaxed ${dark ? 'text-[color:var(--fwm-muted)]' : 'text-[color:var(--fwm-muted)]'}`}
                   data-testid={`team-member-bio-${i}`}>
                  {p.bio}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ChurchNav = ({ church, branding }) => {
  const logo = resolveImage(branding?.logo_url);
  return (
    <nav className="sticky top-0 z-40 backdrop-blur-xl border-b border-[color:var(--fwm-line)]"
         style={{ backgroundColor: 'var(--fwm-nav)' }} data-testid="church-nav">
      <div className="mx-auto max-w-[1180px] px-6 lg:px-10 h-[72px] flex items-center gap-8">
        <Link to={`/church/${church.slug || ''}`}
              className="flex items-center gap-3 min-w-0">
          {logo
            ? <img src={logo} alt={church.name} data-testid="church-logo"
                   className="h-10 w-auto max-w-[140px] object-contain" />
            : <span className="h-10 w-10 grid place-items-center rounded-[3px]
                               border border-[color:var(--fwm-line)]">
                <Church className="h-4 w-4 text-[color:var(--fwm-accent)]" />
              </span>}
          <span className="fwm-display text-base truncate max-w-[240px]">{church.name}</span>
        </Link>
        <div className="hidden md:flex items-center gap-7 ml-auto text-sm
                        text-[color:var(--fwm-muted)]">
          <a href="#about" className="hover:text-[color:var(--fwm-ink)] transition-colors">About</a>
          <Link to={`/leadership/${church.slug || church.id}`} data-testid="nav-leadership-link"
                className="hover:text-[color:var(--fwm-ink)] transition-colors">Our leaders</Link>
          <a href="#events" className="hover:text-[color:var(--fwm-ink)] transition-colors">Events</a>
          <WatchLiveLink
            slug={church.slug || ''}
            className="inline-flex items-center gap-1.5 hover:text-[color:var(--fwm-ink)] transition-colors"
            testId="church-nav-watch-live"
          />
          <a href="#contact" className="hover:text-[color:var(--fwm-ink)] transition-colors">Contact</a>
          <Link to="/shop" data-testid="nav-bookshop-link"
                className="inline-flex items-center gap-1.5 hover:text-[color:var(--fwm-ink)]
                           transition-colors">
            <BookOpen className="h-4 w-4" /> Shop
          </Link>
          <Link to={`/login?type=member&church=${church.slug || ''}&redirect=/member/portal`}
                data-testid="church-nav-member-login"
                className="inline-flex items-center gap-1.5 hover:text-[color:var(--fwm-ink)]
                           transition-colors">
            <LogIn className="h-4 w-4" /> Member login
          </Link>
        </div>
        <Link to="/churches" className="ml-auto md:ml-0 fwm-btn !py-2.5 !px-5 !text-[0.8125rem]"
              data-testid="church-nav-directory">
          All churches <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </nav>
  );
};

/**
 * The hero every church page wears: the banner the church admin uploads under
 * the same dark scrim as the rest of the platform. With nothing uploaded it
 * falls back to FWM's own photography rather than an empty band.
 */
const Hero = ({ hero, church, donate, branding }) => {
  const banner = resolveImage(hero?.banner_url || hero?.image);
  const where = [church.city, church.country].filter(Boolean).join(' · ');
  return (
    <FwmHero
      testId="hero"
      image={banner || FWM_IMAGERY.heroChurches}
      video={hero?.video_url || ''}
      mediaPriority={hero?.video_url ? 'video' : 'image'}
      eyebrow={where || 'Faith World Ministries'}
      title={hero?.headline || church.name}
      accent={hero?.tagline || branding?.tagline || ''}
      blurb={hero?.blurb || branding?.welcome_message || ''}
      actions={(
        <>
          <a href="#events" className="fwm-btn fwm-btn-lg" data-testid="hero-events-link">
            See what&apos;s on <ChevronRight className="h-4 w-4" />
          </a>
          {donate?.enabled !== false && donate?.campaign_slug && (
            <Link to={`/campaigns/${donate.campaign_slug}`} className="fwm-btn-ondark"
                  data-testid="hero-donate-link">
              <Heart className="h-4 w-4" /> Give
            </Link>
          )}
          <ChurchCtaButtons churchId={church.id} churchName={church.name} variant="onDark" />
          <WatchLiveLink
            slug={church.slug || ''}
            className="fwm-btn-ondark inline-flex items-center gap-2"
            testId="hero-watch-live"
          />
        </>
      )}
    >
      {!banner && (
        <p className="mt-6 text-[11px] uppercase tracking-[0.18em] text-white/45"
           data-testid="hero-placeholder-note">
          Ministry photograph — this assembly has not uploaded its own banner yet
        </p>
      )}
    </FwmHero>
  );
};

/**
 * The crest band — the church's own profile picture lifted over the banner,
 * with its name, tagline and where to find it.
 */
const ChurchCrest = ({ church, branding, contact }) => {
  const logo = resolveImage(branding?.logo_url);
  const initials = (church.name || '').split(' ').map((w) => w[0]).slice(0, 2).join('');
  const where = [church.city, church.country].filter(Boolean).join(', ');
  return (
    <div className="relative z-10 mx-auto max-w-[1180px] px-6 lg:px-10 -mt-14 sm:-mt-16"
         data-testid="church-crest">
      <div className="fwm-card p-6 sm:p-8 flex flex-wrap items-center gap-6">
        {logo ? (
          <img src={logo} alt={church.name} data-testid="church-profile-image"
               className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover
                          ring-1 ring-[color:var(--fwm-line)] bg-white" />
        ) : (
          <span data-testid="church-profile-placeholder"
                className="h-20 w-20 sm:h-24 sm:w-24 rounded-full grid place-items-center
                           bg-[color:var(--fwm-ground-2)] border border-[color:var(--fwm-line)]
                           fwm-display text-2xl text-[color:var(--fwm-muted)]">
            {initials || <Church className="h-7 w-7" />}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="fwm-display text-2xl sm:text-3xl leading-tight"
              data-testid="church-crest-name">{church.name}</h2>
          {branding?.tagline && (
            <p className="fwm-kicker mt-2" data-testid="church-crest-tagline">
              {branding.tagline}
            </p>
          )}
          <p className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm
                        text-[color:var(--fwm-muted)]">
            {where && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> {where}
              </span>
            )}
            {contact?.phone && (
              <a href={`tel:${contact.phone}`}
                 className="inline-flex items-center gap-1.5 hover:text-[color:var(--fwm-ink)]">
                <Phone className="h-3.5 w-3.5" /> {contact.phone}
              </a>
            )}
            {contact?.email && (
              <a href={`mailto:${contact.email}`}
                 className="inline-flex items-center gap-1.5 hover:text-[color:var(--fwm-ink)]">
                <Mail className="h-3.5 w-3.5" /> {contact.email}
              </a>
            )}
          </p>
        </div>
        <a href="#contact" className="fwm-btn-ghost shrink-0" data-testid="church-crest-contact">
          Get in touch <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
};

const WelcomeBand = ({ church }) => (
  <section className="bg-[color:var(--fwm-card)] border-y border-[color:var(--fwm-line)]" data-testid="welcome-band">
    <div className="max-w-6xl mx-auto px-4 py-12 grid gap-6 md:grid-cols-[1.4fr,1fr] items-center">
      <div>
        <h2 className="fwm-display text-3xl">Come as you are</h2>
        <p className="text-[color:var(--fwm-body)] mt-2 max-w-xl">
          Whether you'd like to visit one Sunday first or you're ready to call
          {' '}{church.name} home, start here — a real person will follow up with you.
        </p>
      </div>
      <ChurchCtaButtons churchId={church.id} churchName={church.name} />
    </div>
  </section>
);

const AboutSection = ({ about }) => (
  <section id="about" className="max-w-6xl mx-auto px-4 py-14" data-testid="about-section">
    <h2 className="fwm-display text-3xl mb-4">{about?.title || 'About Us'}</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-2 prose prose-slate max-w-none">
        <p className="text-[color:var(--fwm-body)] whitespace-pre-line">{about?.body || ''}</p>
      </div>
      {(about?.pastor_name || about?.pastor_message) && (
        <Card>
          <CardContent className="p-4">
            {about?.pastor_photo_url && (
              <img src={about.pastor_photo_url} alt={about.pastor_name} className="w-full h-40 object-cover rounded-md mb-3" />
            )}
            <p className="text-sm font-semibold text-[color:var(--fwm-ink)]">{about.pastor_name}</p>
            <p className="text-xs text-[color:var(--fwm-body)] mt-2 whitespace-pre-line italic">"{about.pastor_message}"</p>
          </CardContent>
        </Card>
      )}
    </div>
  </section>
);

const EventsSection = ({ events }) => (
  <section id="events" className="bg-[color:var(--fwm-card)] border-y border-[color:var(--fwm-line)]" data-testid="events-section">
    <div className="max-w-6xl mx-auto px-4 py-14">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
        <h2 className="fwm-display text-3xl">{events.title || 'Upcoming Events'}</h2>
        <p className="text-xs text-[color:var(--fwm-muted)]">{events.mode === 'this_church' ? 'Events at this church' : 'All upcoming events'}</p>
      </div>
      {events.items.length === 0 ? (
        <p className="text-sm text-[color:var(--fwm-muted)]">No upcoming events.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.items.map((e) => (
            <Link
              key={e.id}
              to={e.slug ? `/e/${e.slug}` : `/events/${e.id}`}
              className="group fwm-card overflow-hidden hover:border-[color:var(--fwm-accent)] transition-colors"
              data-testid={`event-card-${e.id}`}
            >
              {e.image && <div className="h-32 bg-[color:var(--fwm-ground-2)]" style={{ backgroundImage: `url(${e.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />}
              <div className="p-4">
                {e.start_date && (
                  <p className="text-[11px] text-[var(--cp-primary)] font-semibold uppercase tracking-wide mb-1 flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    {new Date(e.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
                <p className="font-semibold text-[color:var(--fwm-ink)] group-hover:text-[var(--cp-primary)]">{e.name}</p>
                {e.tagline && <p className="text-xs text-[color:var(--fwm-muted)] mt-1 line-clamp-2">{e.tagline}</p>}
                {e.location && (
                  <p className="text-[11px] text-[color:var(--fwm-muted)] mt-2 flex items-center">
                    <MapPin className="h-3 w-3 mr-1" /> {e.location}
                  </p>
                )}
                {e.host_name && e.relation && e.relation !== 'direct' && (
                  <p className="text-[11px] text-[color:var(--fwm-muted)] mt-1"
                     data-testid={`event-card-${e.id}-host`}>
                    {e.relation === 'inherited' ? 'From ' : 'Hosted by '}{e.host_name}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  </section>
);

const GallerySection = ({ gallery }) => {
  if (!gallery?.items?.length) return null;
  return (
    <section id="gallery" className="max-w-6xl mx-auto px-4 py-14" data-testid="gallery-section">
      <h2 className="fwm-display text-3xl mb-6">{gallery.title || 'Photo Gallery'}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {gallery.items.slice(0, 12).map((g, i) => (
          <a key={i} href={g.url} target="_blank" rel="noopener noreferrer" className="block aspect-square overflow-hidden rounded-lg bg-[color:var(--fwm-ground-2)] group" data-testid={`gallery-item-${i}`}>
            <img src={g.url} alt={g.caption || ''} className="w-full h-full object-cover group-hover:scale-105 transition" loading="lazy" />
          </a>
        ))}
      </div>
    </section>
  );
};

const BookshopTeaser = ({ bookshop }) => (
  <section className="bg-amber-50 border-y border-amber-200" data-testid="bookshop-teaser">
    <div className="max-w-6xl mx-auto px-4 py-12 flex flex-col md:flex-row items-center gap-4">
      <BookOpen className="h-12 w-12 text-amber-700 flex-shrink-0" />
      <div className="flex-1 text-center md:text-left">
        <h3 className="text-lg font-bold text-amber-900">{bookshop?.title || 'Bookshop & Sermons'}</h3>
        <p className="text-sm text-amber-800/80 mt-1">Transformative Christian literature, paid sermons & teaching downloads — all in one place.</p>
      </div>
      <Link
        to={bookshop?.url || '/shop'}
        className="fwm-btn"
        data-testid="bookshop-cta"
      >
        {bookshop?.cta_text || 'Visit our Bookshop'} <ChevronRight className="h-4 w-4 ml-1" />
      </Link>
    </div>
  </section>
);

const NewsletterSignup = ({ newsletter, sourceSlug }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const subscribe = useMutation({
    mutationFn: () => fetch(`${API}/api/newsletter/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name, source: sourceSlug }),
    }).then(async (r) => {
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Subscribe failed');
      }
      return r.json();
    }),
    onSuccess: () => {
      toast.success('You\'re subscribed!');
      setEmail(''); setName('');
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <section className="max-w-6xl mx-auto px-4 py-14" data-testid="newsletter-section">
      <div className="fwm-dark-panel text-[#FAF5E8] p-8 md:p-12">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold">{newsletter?.title || 'Stay connected'}</h2>
          <p className="text-sm text-white/70 mt-2">{newsletter?.body || 'Get sermons, devotionals & event news straight to your inbox.'}</p>
          <form
            onSubmit={(e) => { e.preventDefault(); subscribe.mutate(); }}
            className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2"
          >
            <Input placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="bg-white text-[color:var(--fwm-ink)]" data-testid="newsletter-name" />
            <Input type="email" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white text-[color:var(--fwm-ink)]" data-testid="newsletter-email" />
            <Button type="submit" disabled={subscribe.isPending} data-testid="newsletter-submit">
              {subscribe.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Subscribe</>}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

const ContactSection = ({ contact, church, donate }) => (
  <section id="contact" className="bg-[color:var(--fwm-card)] border-t border-[color:var(--fwm-line)]" data-testid="contact-section">
    <div className="max-w-6xl mx-auto px-4 py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
      <div className="md:col-span-1">
        <h2 className="fwm-display text-3xl">Contact</h2>
        <p className="text-sm text-[color:var(--fwm-muted)] mt-2">We'd love to hear from you.</p>
        {donate?.enabled !== false && donate?.campaign_slug && (
          <Link to={`/campaigns/${donate.campaign_slug}`} className="fwm-btn mt-5" data-testid="contact-donate-cta">
            <Heart className="h-4 w-4 mr-1" /> Support our ministry
          </Link>
        )}
      </div>
      <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        {contact?.address && (
          <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-[var(--cp-primary)] mt-0.5" /><div><p className="text-[color:var(--fwm-muted)]">Address</p><p className="text-[color:var(--fwm-ink)]">{contact.address}</p></div></div>
        )}
        {contact?.phone && (
          <div className="flex items-start gap-2"><Phone className="h-4 w-4 text-[var(--cp-primary)] mt-0.5" /><div><p className="text-[color:var(--fwm-muted)]">Phone</p><a href={`tel:${contact.phone}`} className="text-[color:var(--fwm-ink)] hover:underline">{contact.phone}</a></div></div>
        )}
        {contact?.email && (
          <div className="flex items-start gap-2"><Mail className="h-4 w-4 text-[var(--cp-primary)] mt-0.5" /><div><p className="text-[color:var(--fwm-muted)]">Email</p><a href={`mailto:${contact.email}`} className="text-[color:var(--fwm-ink)] hover:underline">{contact.email}</a></div></div>
        )}
        {(contact?.facebook || contact?.youtube || contact?.instagram || contact?.whatsapp) && (
          <div className="flex items-start gap-2"><Globe className="h-4 w-4 text-[var(--cp-primary)] mt-0.5" /><div><p className="text-[color:var(--fwm-muted)] mb-1">Social</p>
            <div className="flex items-center gap-3">
              {contact.facebook && <a href={contact.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook className="h-4 w-4 text-[color:var(--fwm-body)] hover:text-blue-600" /></a>}
              {contact.youtube && <a href={contact.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube"><Youtube className="h-4 w-4 text-[color:var(--fwm-body)] hover:text-red-600" /></a>}
              {contact.instagram && <a href={contact.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><Instagram className="h-4 w-4 text-[color:var(--fwm-body)] hover:text-pink-600" /></a>}
              {contact.whatsapp && <a href={contact.whatsapp.startsWith('http') ? contact.whatsapp : `https://wa.me/${contact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"><MessageCircle className="h-4 w-4 text-[color:var(--fwm-body)] hover:text-emerald-600" /></a>}
            </div>
          </div></div>
        )}
        {contact?.map_embed_url && (
          <div className="sm:col-span-2 mt-4">
            <iframe src={contact.map_embed_url} title={`Map of ${church.name}`} className="w-full h-48 rounded-lg border" loading="lazy" />
          </div>
        )}
      </div>
    </div>
  </section>
);

const Footer = ({ church, contact }) => (
  <footer className="fwm-footer-dark py-10 text-center text-xs">
    <Link to={`/leadership/${church.slug || church.id}`}
          data-testid="footer-leadership-link"
          className="inline-flex items-center gap-1.5 text-white/80 hover:text-white font-medium mb-3">
      <Users className="h-3.5 w-3.5" /> Meet our leaders
    </Link>
    <p>© {new Date().getFullYear()} {church.name}. All rights reserved.</p>
    {contact?.email && <p className="mt-1 opacity-70">{contact.email}</p>}
  </footer>
);

export default PublicChurchPage;
